/**
 * Template merge fields (G1c).
 *
 * ---------------------------------------------------------------------------
 * A MISSING VALUE IS LEFT VISIBLE. IT IS NEVER SUBSTITUTED WITH NOTHING.
 * ---------------------------------------------------------------------------
 * The failure this exists to prevent is "Hi ," arriving in a client's inbox —
 * or worse, "your move from  to  on ". Silently replacing an unknown value with
 * an empty string produces a sentence that is grammatically intact and obviously
 * machine-written, and the rep who sent it never sees what the client saw.
 *
 * So substitution has two outcomes and only two: a real value goes in, or the
 * field stays on screen as a token the compose box highlights. The rep then
 * either types the answer or deletes the sentence. Both are fine; sending a
 * blank where a name belongs is not.
 */

export interface MergeField {
  key: string;
  label: string;
  /** What it looks like in a template body. */
  token: string;
  /** Offered in TEXT (SMS) templates — plain text, and makes sense in a text. */
  sms?: boolean;
}

/** Order is the order the picker offers them in. */
export const MERGE_FIELDS: MergeField[] = [
  { key: 'client_first_name', label: 'Client first name', token: '{{client_first_name}}', sms: true },
  { key: 'client_full_name', label: 'Client full name', token: '{{client_full_name}}', sms: true },
  { key: 'job_number', label: 'Job #', token: '{{job_number}}', sms: true },
  { key: 'origin_city', label: 'Origin city', token: '{{origin_city}}', sms: true },
  { key: 'origin_street', label: 'Origin street (with unit)', token: '{{origin_street}}' },
  { key: 'origin_province', label: 'Origin province', token: '{{origin_province}}' },
  { key: 'origin_postal_code', label: 'Origin postal code', token: '{{origin_postal_code}}' },
  { key: 'destination_city', label: 'Destination city', token: '{{destination_city}}', sms: true },
  { key: 'destination_street', label: 'Destination street (with unit)', token: '{{destination_street}}' },
  { key: 'destination_province', label: 'Destination province', token: '{{destination_province}}' },
  { key: 'destination_postal_code', label: 'Destination postal code', token: '{{destination_postal_code}}' },
  { key: 'move_date', label: 'Move date', token: '{{move_date}}' },
  { key: 'sender_name', label: 'Your name', token: '{{sender_name}}', sms: true },
  { key: 'sender_title', label: 'Your title', token: '{{sender_title}}', sms: true },
  { key: 'sender_phone', label: 'Your phone', token: '{{sender_phone}}', sms: true },
  /**
   * The client inventory portal link, as a ready-made anchor reading
   * "Fill out your inventory online".
   *
   * Its VALUE IS HTML, unlike the plain-text fields. That is safe here and nowhere
   * else: the compose box inserts merge values through insertHtml, so an anchor
   * becomes a real link and survives into the send. It is also the only one of
   * these that should not go in a SUBJECT — the subject is a plain input, and
   * the markup would show up as literal text.
   *
   * Resolved server-side by reusing the lead's newest live inventory_request
   * portal link, or minting one; nothing is emailed either way.
   */
  { key: 'inventory_link', label: 'Inventory link', token: '{{inventory_link}}' },
  /**
   * The two explainer videos — a clickable thumbnail plus a text link, as HTML
   * like {{inventory_link}} and for the same reason.
   *
   * TWO TOKENS RATHER THAN ONE THAT INFERS. A single {{video}} could only
   * choose by lead.move_type, which a fresh website lead has not got — so the
   * long-distance first-call template, used on exactly those leads, would have
   * carried the local film. The template knows which it is; the lead may not.
   */
  { key: 'video_long_distance', label: 'Video — long distance', token: '{{video_long_distance}}' },
  { key: 'video_local', label: 'Video — local', token: '{{video_local}}' },

  /**
   * PLAIN-TEXT FIELDS FOR TEXT TEMPLATES (070), in the same engine. Usable in
   * email too.
   *
   * {{move_when}} is the client-facing "when": the move date on a local move,
   * the PICKUP WINDOW on long distance — never a single date to an LD client
   * until the Confirmation Form ({{confirmed_move_date}}, which is the date on
   * every move type, for messages after the date is confirmed).
   */
  { key: 'move_when', label: 'Move date / pickup window (LD)', token: '{{move_when}}', sms: true },
  { key: 'pickup_window', label: 'Pickup window', token: '{{pickup_window}}', sms: true },
  { key: 'pickup_arrival_window', label: 'Arrival window', token: '{{pickup_arrival_window}}', sms: true },
  { key: 'confirmed_move_date', label: 'Confirmed move date (after the Confirmation Form)', token: '{{confirmed_move_date}}', sms: true },
  { key: 'office_phone', label: 'Office phone', token: '{{office_phone}}', sms: true },
  { key: 'estimate_url', label: 'Estimate link (once sent)', token: '{{estimate_url}}', sms: true },
  { key: 'inventory_url', label: 'Inventory link (URL)', token: '{{inventory_url}}', sms: true },
];

/** The fields a text template may use. */
export const SMS_MERGE_FIELDS: MergeField[] = MERGE_FIELDS.filter((f) => f.sms);

const TOKEN_RE = /\{\{\s*([a-z_]+)\s*\}\}/gi;

export interface FilledTemplate {
  text: string;
  /** Field keys that appear in the text but have no value. */
  missing: string[];
}

/**
 * Fill what we know and leave what we do not.
 *
 * An UNKNOWN token — one that is not a merge field at all, because somebody
 * typed it by hand — is left exactly as written and reported as missing too. It
 * is not our place to delete text a rep put in a template, and highlighting it
 * is how they find the typo.
 */
export function fillMergeFields(template: string, values: Record<string, string | null> | null): FilledTemplate {
  const missing: string[] = [];
  const text = String(template ?? '').replace(TOKEN_RE, (whole, rawKey) => {
    const key = String(rawKey).toLowerCase();
    const value = values?.[key];
    if (value != null && String(value).trim() !== '') return String(value);
    // '' is a value the server chose to leave empty (e.g. {{move_date}} on a
    // long-distance lead): render nothing. null / absent is still "missing".
    if (value === '') return '';
    if (!missing.includes(key)) missing.push(key);
    return whole; // left in place, and highlighted by the caller
  });
  return { text, missing };
}

/** Does this text still carry an unfilled token? The send-time guard. */
export const hasUnfilledFields = (text: string): boolean => {
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(String(text ?? ''));
};

export const fieldLabel = (key: string): string =>
  MERGE_FIELDS.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ');


/**
 * Does an inserted merge value need a space in FRONT of it?
 *
 * Inserting "client first name" and then "job #" produced "Daryl#1000": the
 * editor drops text exactly at the caret, and a rep picking two fields in a row
 * never types the gap between them. So a leading space goes in unless the text
 * before the caret already ends in whitespace — or there is no text before it,
 * which is the start of a line or block and wants no space at all.
 *
 * A trailing space is always added by the caller; it also leaves the caret where
 * the next word goes, so the rep can simply keep typing.
 *
 * PURE, and separated from the DOM on purpose: this is the rule, and the handler
 * that reads the selection is just how it gets its argument.
 */
export function needsLeadingSpace(textBeforeCaret: string | null | undefined): boolean {
  const before = String(textBeforeCaret ?? '');
  if (!before.length) return false;
  return !/\s$/.test(before);
}


// ---------------------------------------------------------------------------
// TEXT (SMS) templates — the same fill, with gaps that cannot be missed
// ---------------------------------------------------------------------------

/**
 * A gap in a filled text: "[no move date]". A text box cannot highlight a
 * token the way the email editor does, so a missing value becomes a bracketed
 * marker in the words themselves. The composer will not send while one is
 * there: nothing goes out half-filled.
 */
export const missingMark = (key: string): string =>
  `[no ${fieldLabel(key).replace(/\s*\(.*\)\s*$/, '').toLowerCase()}]`;

const MARK_RE = /\[no [^\]]+\]/;

/** Fill a text template: values in, each missing value as a [no …] marker. */
export function fillTextTemplate(template: string, values: Record<string, string | null> | null): FilledTemplate {
  const filled = fillMergeFields(template, values);
  const text = filled.text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_w, k) => missingMark(String(k).toLowerCase()));
  return { text, missing: filled.missing };
}

/** Does this text still carry a [no …] gap or an unfilled {{token}}? The send guard. */
export const textHasGaps = (text: string): boolean => MARK_RE.test(String(text ?? '')) || hasUnfilledFields(text);

/**
 * A made-up lead for the Settings preview — never a real client's data. Long
 * distance, so the preview shows the pickup window rule at work.
 */
export const SAMPLE_TEXT_VALUES: Record<string, string | null> = {
  client_first_name: 'Sarah',
  client_full_name: 'Sarah Thompson',
  job_number: '#4821',
  origin_city: 'Toronto',
  destination_city: 'Calgary',
  sender_name: 'Daryl Wells',
  sender_title: 'Moving Consultant',
  sender_phone: '(905) 667-6930',
  move_when: 'Nov 14, 2026 – Nov 16, 2026 · 8–10 AM',
  pickup_window: 'Nov 14, 2026 – Nov 16, 2026 · 8–10 AM',
  pickup_arrival_window: '8–10 AM',
  confirmed_move_date: 'November 15, 2026',
  office_phone: '(647) 931-5244',
  estimate_url: 'https://crm.balticvanlines.ca/estimate/abc123',
  inventory_url: 'https://crm.balticvanlines.ca/document/def456',
};
